import { Button } from "components/ui/button";
import { useAtom, useAtomValue } from "lib/global-context";
import {
  currentStepAtom,
  userIdAtom,
  ageAtom,
  ageEndAtom,
  monthlyExpenseAtom,
  emergencyFundAtom,
} from "lib/atoms";
import Simulator from "../common/simulator";
import BackButton from "../common/back-button";
import InputAge from "./form-fields/input-age";
import InputAgeEnd from "./form-fields/input-age-end";
import InputMonthlyExpense from "./form-fields/input-monthly-expense";
import InputEmergencyFund from "./form-fields/input-emergency-fund";
import {
  isValidAge,
  isValidAgeEnd,
  isValidMonthlyExpenses,
  isValidEmergencyFund,
} from "lib/validation";
import Results from "./results";
import {
  AnswerData,
  finalizeQuestionnaire,
  submitQuestionnaireAnswer,
} from "lib/api";
import { cn, mapQuestionsById } from "lib/utils";
import Disclaimer from "../common/disclaimer";
import { formatAnswers, trackSubmission } from "../common/GTMProvider";
import { getGTMProps } from "types/common";
import styles from "./simulator-form.module.css";

interface SimulatorFormProps {
  questions: {
    questionType: string;
    questionId: string;
    min: number;
    max: number;
    displayConfiguration: {
      type: string;
      title: string;
      placeholder?: string;
      name: string;
      inputType: string;
      description: string;
      errorLocation?: string;
      helperText?: string;
      minErrorText: string;
      maxErrorText: string;
      pageName: string;
    };
  }[];
}

export function SimulatorForm({ questions }: SimulatorFormProps) {
  const questionMap = mapQuestionsById(questions);

  const [currentStep, setCurrentStep] = useAtom(currentStepAtom);
  const userId = useAtomValue(userIdAtom);
  const age = useAtomValue(ageAtom);
  const ageEnd = useAtomValue(ageEndAtom);
  const monthlyExpense = useAtomValue(monthlyExpenseAtom);
  const emergencyFund = useAtomValue(emergencyFundAtom);

  const gtmProps = getGTMProps("retirement");

  const isStep1Valid = (): boolean => {
    return (
      isValidAge(age, questionMap.q1.min, questionMap.q1.max) &&
      isValidAgeEnd(age, ageEnd, questionMap.q2.min, questionMap.q2.max)
    );
  };

  const isStep2Valid = (): boolean => {
    return (
      isValidMonthlyExpenses(
        monthlyExpense,
        questionMap.q3.min,
        questionMap.q3.max,
      ) &&
      isValidEmergencyFund(
        emergencyFund,
        questionMap.q4.min,
        questionMap.q4.max,
      )
    );
  };

  const isFormValid = (): boolean => {
    return isStep1Valid() && isStep2Valid();
  };

  const getAnswerData = (): AnswerData[] => [
    { questionId: questionMap.q1.questionId, value: [age ?? 0] },
    { questionId: questionMap.q2.questionId, value: [ageEnd ?? 0] },
    { questionId: questionMap.q3.questionId, value: [monthlyExpense ?? 0] },
    { questionId: questionMap.q4.questionId, value: [emergencyFund ?? 0] },
  ];

  const goToStep2 = () => {
    const answerData = getAnswerData();
    submitQuestionnaireAnswer(userId, answerData[0], gtmProps);
    submitQuestionnaireAnswer(userId, answerData[1], gtmProps);

    const gtmAnswers = formatAnswers([answerData[0], answerData[1]]);
    trackSubmission({
      formId: `gcb_${userId}`,
      ...gtmProps,
      label: "calculate_now",
      formStep: currentStep,
      quizStep: 1,
      answers: gtmAnswers,
    });

    setCurrentStep(2);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid()) {
      alert("Please ensure all fields are filled out correctly.");
      return;
    }

    const answerData = getAnswerData();
    const p3 = submitQuestionnaireAnswer(userId, answerData[2], gtmProps);
    const p4 = submitQuestionnaireAnswer(userId, answerData[3], gtmProps);
    Promise.all([p3, p4]).then(() => finalizeQuestionnaire(userId, gtmProps));

    const gtmAnswers = formatAnswers([answerData[2], answerData[3]]);
    trackSubmission({
      formId: `gcb_${userId}`,
      ...gtmProps,
      formStep: currentStep,
      quizStep: 1,
      label: "calculate_now",
      answers: gtmAnswers,
    });

    setCurrentStep(3);
  };

  return (
    <Simulator>
      <div className={styles.container}>
        <div
          className={cn(
            styles.innerContainer,
            currentStep === 1 && styles.step1Container,
            currentStep === 2 && styles.step2Container,
          )}
        >
          <div className={styles.textCenter}>
            <div className={styles.fontBold}>Step {currentStep} of 3</div>
            {(currentStep === 1 || currentStep === 2) && (
              <div>
                <h2 className={styles.heading}>
                  Let&rsquo;s shape your vision for retirement.
                </h2>
                <p className={styles.subheading}>
                  Answer a few quick questions to uncover the key steps that
                  will bring your vision to life.
                </p>
              </div>
            )}
          </div>

          {(currentStep === 1 || currentStep === 2) && (
            <form onSubmit={handleSubmit}>
              <div className={styles.form}>
                {currentStep === 1 && (
                  <>
                    <InputAge config={questionMap.q1} />
                    <InputAgeEnd config={questionMap.q2} />
                    <div className={styles.colSpanFull}>
                      <Button onClick={goToStep2} disabled={!isStep1Valid()}>
                        Next
                      </Button>
                    </div>
                  </>
                )}
                {currentStep === 2 && (
                  <>
                    <InputMonthlyExpense config={questionMap.q3} />
                    <InputEmergencyFund config={questionMap.q4} />
                    <div className={styles.colSpanFull}>
                      {currentStep === 2 && <BackButton />}
                      {currentStep === 2 && (
                        <Button type="submit" disabled={!isFormValid()}>
                          {"View your journey"}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </form>
          )}

          {currentStep === 3 && (
            <div>
              <Results
                formData={{
                  age,
                  ageEnd,
                  monthlyExpense,
                  emergencyFund,
                }}
              />
            </div>
          )}

          <div>
            <Disclaimer page="retirement" />
          </div>
        </div>
      </div>
    </Simulator>
  );
}
